(() => {
  if (window.ArtboxRecommendations) return;

  const railSelector = '[data-artbox-recommendations-grid]';
  const previousSelector = '[data-artbox-recommendations-prev]';
  const nextSelector = '[data-artbox-recommendations-next]';
  const progressSelector = '[data-artbox-recommendations-progress]';
  const lazySelector = '[data-artbox-collection-script]';
  const collectionKey = Symbol.for('artbox.collection.lifecycle');
  const watched = new WeakSet();
  const ready = new WeakSet();
  const handlers = new WeakMap();
  const railPositions = new WeakMap();
  let collectionRequest;

  const finishCollection = (root) => {
    if (!root.isConnected || !window[collectionKey] || ready.has(root)) return;
    window[collectionKey].initialise(root);
    ready.add(root);
    observer?.unobserve(root);
    const handler = handlers.get(root);
    for (const type of ['pointerover', 'pointerdown', 'focusin']) root.removeEventListener(type, handler);
    handlers.delete(root);
  };

  const loadCollection = (root) => {
    if (!root.isConnected || ready.has(root)) return;
    if (window[collectionKey]) {
      finishCollection(root);
      return;
    }
    if (!collectionRequest) {
      collectionRequest = new Promise((resolve, reject) => {
        const url = new URL(root.dataset.artboxCollectionScript, document.baseURI).href;
        const script = document.createElement('script');
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', () => {
          script.remove();
          reject(new Error('Collection script unavailable'));
        }, { once: true });
        script.src = url;
        script.async = true;
        document.head.append(script);
      }).catch(() => { collectionRequest = undefined; });
    }
    collectionRequest.then(() => {
      finishCollection(root);
    });
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) loadCollection(entry.target); });
    }, { rootMargin: '600px 0px' }) : null;

  const watchCollection = (root) => {
    if (watched.has(root)) return;
    watched.add(root);
    const handler = () => loadCollection(root);
    handlers.set(root, handler);
    root.addEventListener('pointerover', handler, { passive: true });
    root.addEventListener('pointerdown', handler, { passive: true });
    root.addEventListener('focusin', handler);
    if (observer && !window.Shopify?.designMode) observer.observe(root);
    else loadCollection(root);
  };

  const measurePositions = (rail) => {
    const cards = [...rail.querySelectorAll('.artbox-collection-card')].filter((card) => card.getBoundingClientRect().width > 0);
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const firstLeft = cards[0]?.getBoundingClientRect().left || 0;
    const positions = [0];
    cards.forEach((card) => {
      const position = Math.min(maximum, Math.max(0, card.getBoundingClientRect().left - firstLeft));
      if (position - positions[positions.length - 1] > 2) positions.push(position);
    });
    railPositions.set(rail, positions);
  };

  const updateControls = (rail) => {
    const root = rail.closest('.artbox-product--recommendations');
    const previous = root?.querySelector(previousSelector);
    const next = root?.querySelector(nextSelector);
    if (!previous || !next) return;
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    previous.disabled = rail.scrollLeft <= 2;
    next.disabled = rail.scrollLeft >= maximum - 2;
    const progress = root.querySelector(progressSelector);
    if (!progress) return;
    const positions = railPositions.get(rail) || [0];
    progress.hidden = positions.length < 2;
    if (progress.children.length !== positions.length) {
      const dots = positions.map(() => {
        const dot = document.createElement('span');
        dot.className = 'artbox-recommendations__dot';
        dot.setAttribute('aria-hidden', 'true');
        return dot;
      });
      progress.replaceChildren(...dots);
    }
    const active = positions.reduce((closest, position, index) =>
      Math.abs(position - rail.scrollLeft) < Math.abs(positions[closest] - rail.scrollLeft) ? index : closest, 0);
    progress.setAttribute('aria-valuemax', String(positions.length));
    progress.setAttribute('aria-valuenow', String(active + 1));
    [...progress.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
  };

  const initializeRail = (rail) => {
    if (rail.dataset.artboxRecommendationsReady === 'true') return;
    rail.dataset.artboxRecommendationsReady = 'true';
    rail.addEventListener('scroll', () => updateControls(rail), { passive: true });
    const refresh = () => {
      measurePositions(rail);
      updateControls(rail);
    };
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(refresh);
      observer.observe(rail);
      rail.querySelectorAll('.artbox-collection-card').forEach((card) => observer.observe(card));
    }
    requestAnimationFrame(refresh);
  };

  const initialize = (root = document) => {
    if (root.matches?.(lazySelector)) watchCollection(root);
    root.querySelectorAll?.(lazySelector).forEach(watchCollection);
    if (root.matches?.(railSelector)) initializeRail(root);
    root.querySelectorAll?.(railSelector).forEach(initializeRail);
  };

  const moveRail = (button, direction) => {
    const root = button.closest('.artbox-product--recommendations');
    const rail = root?.querySelector(railSelector);
    const firstCard = rail?.querySelector('.artbox-collection-card');
    if (!rail || !firstCard) return;
    const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
    rail.scrollBy({
      left: direction * (firstCard.getBoundingClientRect().width + gap),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  document.addEventListener('click', (event) => {
    const previous = event.target.closest(previousSelector);
    const next = event.target.closest(nextSelector);
    if (previous) moveRail(previous, -1);
    if (next) moveRail(next, 1);
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(lazySelector)) observer?.unobserve(event.target);
    event.target.querySelectorAll?.(lazySelector).forEach((root) => observer?.unobserve(root));
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.ArtboxRecommendations = { initialize };
})();
